import { useRef, useState } from "react";
import { FileDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const CustomerImportButton = ({ onDone }) => {
  const ref = useRef();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/customers/upload", fd);
      setRes(data);
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => { setRes(null); setOpen(true); }} data-testid="import-customers-btn"><Upload className="mr-1 h-4 w-4" /> Import customers</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="import-customers-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#01195D]">Bulk import customers</DialogTitle>
            <DialogDescription>
              Excel (.xlsx) or CSV. Columns: <span className="num">Name</span> (required), Contact Name, Email, Phone, ABN, Address, Payment Terms, Notes.
              Existing customers are matched by ABN (or by name) and updated; others are added.
            </DialogDescription>
          </DialogHeader>
          <div
            className="rounded-lg border-2 border-dashed border-slate-300 p-8 text-center transition-colors hover:border-[#1BA3E0]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files[0]); }}
            data-testid="import-customers-dropzone"
          >
            <input ref={ref} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="import-customers-file-input" />
            <div className="flex justify-center gap-2">
              <Button disabled={busy} onClick={() => ref.current.click()} className="bg-[#01195D] hover:bg-[#0A2A7A]" data-testid="import-customers-choose-btn">{busy ? "Uploading…" : "Choose file"}</Button>
              <Button variant="outline" onClick={() => downloadFile("/templates/customers", "customers_template.csv")} data-testid="import-customers-template-btn"><FileDown className="mr-1 h-4 w-4" /> Template</Button>
            </div>
            <p className="mt-3 text-xs text-slate-500">or drag and drop the file here</p>
          </div>
          {res && (
            <div className="rounded-lg bg-slate-50 p-4 text-sm" data-testid="import-customers-result">
              <div className="flex gap-6">
                <span>Rows <b className="num">{res.rows}</b></span>
                <span className="text-emerald-700">New <b className="num">{res.created}</b></span>
                <span className="text-blue-700">Updated <b className="num">{res.updated}</b></span>
                <span className="text-amber-700">Skipped <b className="num">{res.skipped}</b></span>
              </div>
              {res.errors?.length > 0 && <ul className="mt-2 list-disc pl-5 text-amber-800">{res.errors.slice(0, 8).map((e) => <li key={e}>{e}</li>)}</ul>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
